import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { AffiliateOnboardingModal } from './modals/AffiliateOnboardingModal';
import { userPreferencesService } from '../services/userPreferencesService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const MAX_SHOW_COUNT = 5;
const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours en millisecondes

export const AffiliateOnboardingManager: React.FC = () => {
  const location = useLocation();
  const { user, userCreatedAt, isTrial, isHomePromo, loading } = useAuth();
  const { subscriptionTier, isInitialized } = useSubscription();
  const [showModal, setShowModal] = useState(false);
  
  // Utiliser une ref pour garder une trace du montage et éviter les race conditions
  const isMounted = useRef(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (isTrial || isHomePromo) {
      setShowModal(false);
      hasChecked.current = true;
    }
  }, [loading, isTrial, isHomePromo]);

  useEffect(() => {
    let isCancelled = false;

    const checkAffiliateOnboarding = async () => {
      // Éviter les vérifications multiples
      if (hasChecked.current) return;

      if (loading) {
        return;
      }
      
      // Attendre que le statut d'abonnement soit chargé
      if (!isInitialized) {
        return;
      }

      // Ne pas afficher si l'utilisateur n'est pas connecté
      if (!user) {
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));

        if (isCancelled || !isMounted.current) return;

        const userData = userSnap.exists() ? userSnap.data() as Record<string, unknown> : null;
        const isTrialFromDoc = userData?.isTrial === true || userData?.isTrial === 'true';
        const isHomePromoFromDoc = userData?.homePromo === true || userData?.homePromo === 'true';

        if (isTrial || isHomePromo || isTrialFromDoc || isHomePromoFromDoc) {
          setShowModal(false);
          hasChecked.current = true;
          return;
        }
      } catch {
        if (isTrial || isHomePromo) {
          setShowModal(false);
          hasChecked.current = true;
          return;
        }
      }

      // Ne pas afficher si le compte a été créé il y a moins d'une semaine
      if (userCreatedAt) {
        const accountAge = Date.now() - userCreatedAt.getTime();
        if (accountAge < ONE_WEEK_IN_MS) {
          return;
        }
      }

      // Ne pas afficher sur les pages publiques, de landing, dashboard affiliation, changelog ou help
      const publicPaths = [
        '/', '/login', '/register', '/pricing', '/affiliation', '/affiliate', 
        '/changelog', '/brokers', '/terms', '/contact', '/privacy', 
        '/security', '/affiliate-terms'
      ];
      if (publicPaths.includes(location.pathname) || 
          location.pathname === '/affiliate/dashboard' ||
          location.pathname.startsWith('/help')) {
        return;
      }

      // Ne pas afficher si l'utilisateur a déjà une licence à vie
      if (subscriptionTier === 'LIFETIME') {
        return;
      }

      // Ne pas afficher si l'utilisateur est en période d'essai
      if (isTrial || isHomePromo) {
        return;
      }

      // Vérifier les préférences utilisateur
      try {
        const prefs = await userPreferencesService.getUserPreferences();
        
        // Vérifier si l'effet a été annulé pendant l'attente
        if (isCancelled || !isMounted.current) return;

        const onboardingPrefs = prefs.affiliateOnboarding;
        
        // Si pas encore de préférences, initialiser
        if (!onboardingPrefs) {
          setShowModal(true);
          hasChecked.current = true;
          return;
        }

        // Vérifier le nombre max d'affichages
        if (onboardingPrefs.showCount >= MAX_SHOW_COUNT) {
          return;
        }

        // Vérifier si au moins 1 semaine s'est écoulée depuis le dernier affichage
        if (onboardingPrefs.lastShownDate) {
          const lastShown = new Date(onboardingPrefs.lastShownDate).getTime();
          const now = Date.now();
          const timeDiff = now - lastShown;
          
          if (timeDiff < ONE_WEEK_IN_MS) {
            // Moins d'une semaine s'est écoulée, ne pas afficher
            return;
          }
        }

        // Toutes les conditions sont remplies, afficher le modal
        hasChecked.current = true;
        setShowModal(true);
        
      } catch (e) {
        // console.error supprimé
      }
    };

    // Attendre un peu pour ne pas afficher le modal immédiatement au chargement
    const timer = setTimeout(() => {
      checkAffiliateOnboarding();
    }, 2000);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [location.pathname, user, userCreatedAt, subscriptionTier, isInitialized, isTrial, isHomePromo, loading]);

  const handleCloseModal = async () => {
    setShowModal(false);
    
    if (user) {
      try {
        const prefs = await userPreferencesService.getUserPreferences();
        const currentOnboarding = prefs.affiliateOnboarding || {
          showCount: 0,
          lastShownDate: null,
          declinedDates: []
        };
        
        const now = new Date().toISOString();
        
        await userPreferencesService.updateUserPreferences({
          affiliateOnboarding: {
            ...currentOnboarding,
            showCount: (currentOnboarding.showCount || 0) + 1,
            lastShownDate: now
          }
        });
      } catch (error) {
        // console.error supprimé
      }
    }
  };

  const handleDecline = async () => {
    if (user) {
      try {
        const prefs = await userPreferencesService.getUserPreferences();
        const currentOnboarding = prefs.affiliateOnboarding || {
          showCount: 0,
          lastShownDate: null,
          declinedDates: []
        };
        
        const now = new Date().toISOString();
        const declinedDates = [...(currentOnboarding.declinedDates || []), now];
        
        await userPreferencesService.updateUserPreferences({
          affiliateOnboarding: {
            ...currentOnboarding,
            showCount: (currentOnboarding.showCount || 0) + 1,
            lastShownDate: now,
            declinedDates
          }
        });
      } catch (error) {
        // console.error supprimé
      }
    }
  };

  return (
    <AffiliateOnboardingModal 
      isOpen={showModal && !loading && !isTrial && !isHomePromo} 
      onClose={handleCloseModal}
      onDecline={handleDecline}
    />
  );
};

export default AffiliateOnboardingManager;
